<template>
  <modal-fullscreen v-if="visible" :visible="visible" width="1200px" title="Send Invitation" @cancel="closeModal">
    <template #default="{ isFullscreen }">
      <div
        class="invite-user__layout"
        :class="{
          'invite-user__layout--fullscreen': isFullscreen,
          'invite-user__layout--without-gaps': !isFacilityCompany
        }"
      >
        <div class="invite-user__form-column">
          <a-form-model
            layout="horizontal"
            ref="form"
            class="flex gap-2"
            :model="form"
            :rules="rules"
            autocomplete="off"
            :label-col="{ span: 10 }"
            :wrapper-col="{ span: 14 }"
          >
            <a-alert
              message="Complete the form below. We will send an invitation to join the company. They will get an email or alert."
              type="info"
              show-icon
            />

            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Email"
              prop="email"
              extra="Email address that receives the invitation. Enter a valid email address."
            >
              <a-input v-model="form.email" placeholder="Email" />
            </a-form-model-item>
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Phone"
              prop="phone"
              extra="Phone number linked to the invitation. Use international format: + followed by 7–18 digits."
            >
              <phone-input
                @input="
                  value => {
                    $refs.form.validateField('phone')
                    form.phone = value
                  }
                "
                :value="form.phone"
              />
            </a-form-model-item>
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Message"
              prop="message"
              extra="Personal message included in the invitation email."
            >
              <a-textarea v-model="form.message" placeholder="Message" />
            </a-form-model-item>
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Suggested Associate ID"
              prop="associate_number"
              extra="Six-digit unique identifier assigned to the Associate in this Company."
            >
              <a-input v-model="form.associate_number" placeholder="Suggested Associate ID" autocomplete="off" />
            </a-form-model-item>
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Position"
              prop="role"
              extra="Position assigned to the Associate in this Company."
            >
              <custom-select
                :multiple="false"
                :allow-clear="false"
                :value.sync="form.role"
                placeholder="Position"
                :options="roles"
                name="name"
                optionKey="id"
              />
            </a-form-model-item>
            <a-form-model-item
              :colon="false"
              label-align="left"
              label="Schedule Type"
              prop="schedule_type"
              extra="Strict follows assigned shift times; Flexible supports self-scheduling; Free has no preset schedule."
            >
              <a-select v-model="form.schedule_type" placeholder="Enter schedule type">
                <a-select-option v-for="(value, key) in scheduleTypes" :key="key" :value="key">
                  {{ value }}
                </a-select-option>
              </a-select> </a-form-model-item
            ><a-form-model-item
              :colon="false"
              label-align="left"
              label="Payment Type"
              prop="payment_info.type"
              extra="Employee—an employment relationship; Contractor—work through a Legal Entity; Volunteer—an unpaid role."
            >
              <a-select v-model="form.payment_info.type" placeholder="Enter payment type">
                <a-select-option v-for="(value, key) in paymentTypes" :key="key" :value="key">
                  {{ value }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
            <template v-if="form.payment_info.type !== 'Volunteer'">
              <a-form-model-item
                :colon="false"
                label-align="left"
                label="Payment Method"
                prop="payment_info.method"
                extra="Salary—a fixed amount per pay period; Hourly—an amount per worked hour; Marketplace Rate—a rate supplied by Marketplace."
              >
                <a-select v-model="form.payment_info.method" placeholder="Enter payment method">
                  <a-select-option
                    v-for="(value, key) in paymentMethods"
                    :key="key"
                    :disabled="isDisabledPaymentMethod && key === 'marketplace_rate'"
                    :value="key"
                  >
                    {{ value }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>

              <a-form-model-item
                :colon="false"
                label-align="left"
                label="Payment Status"
                prop="payment_info.status"
                extra="On Call / Per Diem—as-needed work; Full Time—regular full-time work; Part Time—regular reduced-hours work."
              >
                <a-select v-model="form.payment_info.status" placeholder="Enter payment status">
                  <a-select-option v-for="(value, key) in paymentStatuses" :key="key" :value="key">
                    {{ value }}
                  </a-select-option>
                </a-select> </a-form-model-item
              ><a-form-model-item
                v-if="form.payment_info.type === 'Contractor'"
                :colon="false"
                label-align="left"
                label="Legal Entity"
                prop="payment_info.legal_entity"
                extra="Legal Entity responsible for the Contractor’s payment and business records."
              >
                <custom-select
                  :multiple="false"
                  :allow-clear="false"
                  :value.sync="form.payment_info.legal_entity"
                  placeholder="Legal Entity"
                  :options="agencies"
                  name="name"
                  optionKey="id"
                >
                  <template #option="{ option }"> [{{ option.id }}] - {{ option.name }}</template>
                </custom-select>

                <a-button @click="handleOpenLegalEntity" type="primary" class="w-full"
                  >Create New</a-button
                >
              </a-form-model-item>
              <a-form-model-item
                v-if="isVisibleRate"
                :colon="false"
                label="Hourly Rate, $"
                label-align="left"
                prop="payment_info.hourly_rate"
                extra="Base amount paid per worked hour, in US dollars."
              >
                <a-input-number v-model="rateProxy" :min="0" placeholder="Enter rate" />
              </a-form-model-item>

              <a-form-model-item
                v-if="isVisibleRate && form.payment_info.method === 'salary'"
                :colon="false"
                label="Salary for Pay Period, $"
                label-align="left"
                prop="payment_info.salary"
                extra="Fixed salary amount for one pay period, in US dollars."
              >
                <a-input-number v-model="salaryProxy" :min="0" placeholder="Enter salary" />
              </a-form-model-item>
              <a-form-model-item
                :colon="false"
                label="Orientation Rate, $"
                label-align="left"
                prop="payment_info.orientation_rate"
                extra="Amount paid per orientation hour, in US dollars."
              >
                <a-input-number v-model="orientationRateProxy" :min="0" placeholder="Enter Orientation Rate" />
              </a-form-model-item>

              <a-form-model-item
                :colon="false"
                label="Training Rate, $"
                label-align="left"
                prop="payment_info.training_rate"
                extra="Amount paid per training hour, in US dollars."
              >
                <a-input-number v-model="trainingRateProxy" :min="0" placeholder="Enter Training Rate" />
              </a-form-model-item>
              <template v-if="isVisibleWorkingHoursLimits">
                <a-form-model-item
                  :colon="false"
                  label-align="left"
                  label="Max Working Hours Per Day"
                  prop="max_working_hours_per_day"
                  extra="Select 8, 12, or 16 hours, or leave empty when no daily limit is needed."
                >
                  <a-select
                    v-model="dailyWorkingHoursLimitValue"
                    :allow-clear="true"
                    placeholder="Max Working Hours Per Day"
                  >
                    <a-select-option
                      v-for="option in dailyWorkingHoursOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>

                <a-form-model-item
                  :colon="false"
                  label-align="left"
                  label="Max Working Hours Per Week"
                  prop="max_working_hours_per_week"
                  extra="Select 40, 60, or 84 hours, or choose Custom to enter another weekly limit."
                >
                  <a-select v-model="weeklyWorkingHoursSelectValue" placeholder="Max Working Hours Per Week">
                    <a-select-option
                      v-for="option in weeklyWorkingHoursOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </a-select-option>
                    <a-select-option :value="customWeeklyWorkingHours"> Custom </a-select-option>
                  </a-select>
                  <a-input-number
                    v-if="weeklyWorkingHoursSelectValue === customWeeklyWorkingHours"
                    v-model="form.max_working_hours_per_week"
                    class="mt-2"
                    :min="0"
                    placeholder="Enter custom hours"
                  />
                </a-form-model-item>
              </template>
              <a-form-model-item
                :colon="false"
                label-align="left"
                :label="
                  (form.payment_info.type ?? '').toLowerCase() === 'contractor'
                    ? 'Send to Orientation'
                    : 'Send to Onboarding'
                "
                :extra="
                  (form.payment_info.type ?? '').toLowerCase() === 'contractor'
                    ? 'When on, the Contractor enters Orientation after accepting the invitation. When off, Orientation is skipped.'
                    : 'When on, the Associate enters Onboarding after accepting the invitation. When off, Onboarding is skipped.'
                "
              >
                <a-switch v-model="form.sent_to_onboarding" /> </a-form-model-item
            ></template>

            <a-alert v-if="!hasOfferLetters" type="info" show-icon>
              <template slot="message"
                >You need to configure the Contractor Invite and Employee Invite before sending out invitations!

                <RouterLink to="/" :style="{ color: '#2563eb', cursor: 'pointer' }"
                  >Configure now.</RouterLink
                ></template
              ></a-alert
            >
          </a-form-model>
        </div>

        <aside v-if="isFacilityCompany" class="invite-user__gaps-column">
          <available-regular-schedule-gaps-modal embedded :visible="visible" :role-name="selectedRoleName" />
        </aside>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-content-between">
        <a-button key="back" @click="closeModal">Cancel</a-button>
        <a-button :disabled="!hasOfferLetters" key="submit" type="primary" @click="save" :loading="loading"
          >Send</a-button
        >
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import { Alert, Button, FormModel, Input, InputNumber, Modal, notification, Select, Switch } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import customSelectVue from '../../../components/ui/custom-select.vue'
import { deepCopy } from '@/utils/deep-copy'
import PhoneInput from '@/components/ui/phone-input.vue'
import AvailableRegularScheduleGapsModal from './available-regular-schedule-gaps-modal.vue'
import {
  CUSTOM_WEEKLY_WORKING_HOURS,
  WEEKLY_WORKING_HOURS_OPTIONS,
  getDailyWorkingHoursOptions,
  getWeeklyWorkingHoursSelectValue,
  isWorkingHoursPaymentType
} from '@/modules/profile/utils/working-hours-limits'

const { mapActions: userActions } = createNamespacedHelpers('user')
const { mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

const scheduleTypes = Object.freeze({
  fixed: 'Strict',
  self_schedule: 'Flexible',
  free: 'Free'
})

const paymentTypes = Object.freeze({
  Employee: 'Employee',
  Contractor: 'Contractor',
  Volunteer: 'Volunteer'
})

const paymentStatuses = Object.freeze({
  on_call: 'On Call / Per Diem',
  full_time: 'Full Time',
  part_time: 'Part Time'
})

const paymentMethods = Object.freeze({
  salary: 'Salary',
  hourly: 'Hourly',
  marketplace_rate: 'Marketplace Rate'
})

const initialForm = {
  email: undefined,
  phone: undefined,
  message:
    'You`ve been invited to join our team! As a part of our company, you can track your work and collaborate with us.',
  role: undefined,
  payment_info: {
    type: undefined,
    method: 'hourly',
    status: undefined,
    hourly_rate: 0,
    legal_entity: undefined,
    salary: 0,
    training_rate: 0,
    orientation_rate: 0
  },
  schedule_type: 'fixed',
  associate_number: undefined,
  max_working_hours_per_day: 0,
  max_working_hours_per_week: 0,
  sent_to_onboarding: true
}

const initialRules = {
  email: [
    {
      required: true,
      message: 'Email can not be empty',
      trigger: 'change',
      type: 'email'
    }
  ],
  phone: [
    {
      required: true,
      message: 'Phone can not be empty',
      trigger: 'change'
    },
    {
      transform: value => (value ? value.replace(/\s+/g, '') : value),
      pattern: /^\+\d{7,18}$/,
      message: 'Phone number must start with + and contain 7–18 digits.'
    }
  ],
  role: [
    {
      required: true,
      message: 'Position can not be empty',
      trigger: 'change'
    }
  ],
  schedule_type: [
    {
      required: true,
      message: 'Schedule type can not be empty',
      trigger: 'change'
    }
  ],
  payment_info: {
    type: [
      {
        required: true,
        message: 'Payment type can not be empty',
        trigger: 'change'
      }
    ],
    method: [
      {
        required: true,
        message: 'Payment method can not be empty',
        trigger: 'change'
      }
    ],
    status: [
      {
        required: true,
        message: 'Payment status can not be empty',
        trigger: 'change'
      }
    ],
    legal_entity: [
      {
        required: true,
        message: 'Legal Entity can not be empty',
        trigger: 'change'
      }
    ],
    salary: [
      {
        required: true,
        message: 'Salary can not be empty',
        trigger: 'change',
        type: 'number'
      },
      {
        min: 1,
        message: 'Salary can not be empty',
        trigger: 'change',
        type: 'number'
      }
    ]
  }
}

export default {
  components: {
    'a-button': Button,
    'a-input': Input,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-form-model-item': FormModel.Item,
    'a-form-model': FormModel,
    'a-input-number': InputNumber,
    'a-alert': Alert,
    'a-textarea': Input.TextArea,
    'a-switch': Switch,
    'custom-select': customSelectVue,
    'phone-input': PhoneInput,
    'available-regular-schedule-gaps-modal': AvailableRegularScheduleGapsModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associate: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      form: { ...initialForm },
      rules: {
        ...initialRules,
        associate_number: [
          {
            required: true,
            message: 'Associate number can not be empty',
            trigger: 'change'
          },
          {
            validator: (_, value) => {
              if (!value || /^\d+$/.test(value)) {
                return Promise.resolve()
              }
              return Promise.reject(new Error('Only digits are allowed.'))
            },
            trigger: 'change'
          },
          {
            len: 6,
            message: 'The associate number must be 6 characters.',
            trigger: 'change'
          },
          {
            validator: this.associateNumberValidator,
            trigger: 'change'
          }
        ]
      },

      isInitializing: true,
      paymentMethods: paymentMethods,
      paymentStatuses: paymentStatuses,
      paymentTypes: paymentTypes,
      scheduleTypes: scheduleTypes,
      weeklyWorkingHoursOptions: WEEKLY_WORKING_HOURS_OPTIONS,
      customWeeklyWorkingHours: CUSTOM_WEEKLY_WORKING_HOURS,
      isCustomWeeklyWorkingHours: false,

      loading: false
    }
  },
  computed: {
    isVisibleWorkingHoursLimits() {
      return isWorkingHoursPaymentType(this.form.payment_info.type)
    },
    dailyWorkingHoursOptions() {
      return getDailyWorkingHoursOptions()
    },
    dailyWorkingHoursLimitValue: {
      get() {
        return this.form.max_working_hours_per_day || undefined
      },
      set(value) {
        this.form.max_working_hours_per_day = value ?? 0
      }
    },
    weeklyWorkingHoursSelectValue: {
      get() {
        if (this.isCustomWeeklyWorkingHours) {
          return CUSTOM_WEEKLY_WORKING_HOURS
        }

        return getWeeklyWorkingHoursSelectValue(this.form.max_working_hours_per_week)
      },
      set(value) {
        if (value === CUSTOM_WEEKLY_WORKING_HOURS) {
          this.isCustomWeeklyWorkingHours = true

          return
        }

        this.isCustomWeeklyWorkingHours = false
        this.form.max_working_hours_per_week = value === undefined ? 0 : value
      }
    },
    isVisibleRate() {
      switch (this.form.payment_info.type) {
        case 'Employee': {
          return !!this.form.payment_info.method
        }
        case 'Volunteer': {
          return false
        }
        case 'Contractor': {
          return true
        }
        default:
          return false
      }
    },
    isDisabledPaymentMethod() {
      return !(
        this.form.payment_info.type === 'Contractor' && this.$store.state.applicationState.currentProject.staffing
      )
    },
    isFacilityCompany() {
      return !this.$store.state.applicationState.currentProject.staffing
    },
    hasOfferLetters() {
      return (
        this.$store.state.applicationState.currentProject.contractor_offer_letter_id &&
        this.$store.state.applicationState.currentProject.employee_offer_letter_id
      )
    },
    agencies() {
      return this.$store.state.applicationState.projectLegalEntities
    },
    roles() {
      return this.$store.state.applicationState.projectRoles
    },
    selectedRoleName() {
      return this.roles.find(role => role.id === this.form.role)?.name
    },
    rateProxy: {
      get() {
        return this.form.payment_info.hourly_rate / 100
      },
      set(value) {
        this.form.payment_info.hourly_rate = Math.round(value * 100)
      }
    },

    salaryProxy: {
      get() {
        return (this.form.payment_info?.salary || 0) / 100
      },
      set(value) {
        this.$set(this.form.payment_info, 'salary', Math.round(value * 100))
      }
    },

    orientationRateProxy: {
      get() {
        return this.form.payment_info.orientation_rate / 100
      },
      set(value) {
        this.form.payment_info.orientation_rate = Math.round(value * 100)
      }
    },
    trainingRateProxy: {
      get() {
        return this.form.payment_info.training_rate / 100
      },
      set(value) {
        this.form.payment_info.training_rate = Math.round(value * 100)
      }
    }
  },
  watch: {
    'form.payment_info.type': {
      handler(newVal) {
        if (this.isInitializing) return

        this.form.payment_info.legal_entity = undefined
        this.form.payment_info.method = undefined
        this.form.payment_info.status = undefined
        this.form.payment_info.hourly_rate = 0

        if (newVal === 'Contractor') this.fillDefaultRates(this.form?.associate?.role?.id)

        this.normalizeWorkingHoursLimits()
      }
    },
    'form.payment_info.method': {
      handler() {
        if (this.isInitializing) return

        this.form.payment_info.status = undefined
      }
    },
    'form.role': {
      handler(newVal) {
        this.fillDefaultRates(newVal)
      }
    },
    visible: {
      handler(newVal) {
        this.$nextTick(() => {
          this.isInitializing = false
        })

        if (this.associate && newVal) {
          const { id, ...user } = this.associate

          this.isInitializing = true
          this.isCustomWeeklyWorkingHours = false
          this.form = deepCopy({ ...initialForm, ...user })

          this.form.role = user.role?.id
          this.form.payment_info = {
            ...initialForm.payment_info,
            ...user?.payment_info,
            legal_entity: user?.payment_info?.legal_entity?.id
          }

          this.form.message =
            'You`ve been invited to join our team! As a part of our company, you can track your work and collaborate with us.'

          this.normalizeWorkingHoursLimits()
        }

        if (!newVal) {
          this.isInitializing = true
          this.isCustomWeeklyWorkingHours = false
          this.form = deepCopy(initialForm)
        }
      },
      immediate: true
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...userActions(['inviteUser']),
    ...profileActions(['checkAvailableAssociateNumber']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    fillDefaultRates(id) {
      if (!id) return

      const currentRole = this.roles.find(role => role.id === id)
      if (!currentRole) return

      const fields = ['hourly_rate', 'orientation_rate', 'training_rate']

      const paymentInfo = this.form.payment_info

      fields.forEach(field => {
        if (!paymentInfo[field]) {
          paymentInfo[field] = currentRole[field] ?? 0
        }
      })
    },

    normalizeWorkingHoursLimits() {
      if (!this.isVisibleWorkingHoursLimits) {
        this.form.max_working_hours_per_day = 0
        this.form.max_working_hours_per_week = 0
        this.isCustomWeeklyWorkingHours = false

        return
      }

      const hasDailyOption = this.dailyWorkingHoursOptions.some(
        option => option.value === this.form.max_working_hours_per_day
      )

      if (this.form.max_working_hours_per_day && !hasDailyOption) {
        this.form.max_working_hours_per_day = 0
      }
    },

    handleOpenLegalEntity() {

      this.openModal({
        type: 'legalEntity',
        data: {
          visible: true,
          initialValues: {
            type: 'independent_contractor',
            address: deepCopy(
              this.associate?.address ?? {
                address_line1: undefined,
                address_line2: undefined,
                city: undefined,
                state: 'CA',
                country: 'US',
                zipcode: undefined
              }
            ),
            name: [this.associate?.first_name, this.associate?.last_name].filter(Boolean).join(' ') || undefined,
            tax_id: (this.associate?.social_security_number ?? '').replace(/\D/g, '') || undefined,
            admin_ids: this.associate?.id ? [this.associate.id] : []
          },
          add: async legal_entity => {
            this.form.payment_info.legal_entity = legal_entity.id
          },
          showCreateAssociate: false
        }
      })
    },

    closeModal() {
      this.$emit('update:associate', undefined)
      this.$emit('update:visible', false)
    },

    async associateNumberValidator(rule, value, callback) {
      try {
        if (this.associate?.associate_number === value) {
          return
        }

        const result = await this.checkAvailableAssociateNumber({
          associate_number: value
        })

        if (result.owner) {
          callback(
            new Error(
              `This associate number is already occupied by another user in this company. You can use available associate number: ${result.available_number} `
            )
          )
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },
    async save() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }

        this.confirmInvite()
      })
    },
    confirmInvite() {
      Modal.confirm({
        title: 'Check the pay details before sending',
        content:
          'The offer letter is built from what you entered. Check the financial details — Payment Type, Payment Method, Payment Status, Legal Entity, and the rates.',
        okText: 'Send invite',
        cancelText: 'Keep editing',
        maskClosable: false,
        onOk: () => this.submitInvite(),
        onCancel() {}
      })
    },
    async submitInvite() {
      this.loading = true

      const payload = {
        user: {
          ...this.form,
          associate_id: this.associate?.id,
          email: this.form.email.toLowerCase(),
          payment_info: {
            ...this.form.payment_info,
            legal_entity: { id: this.form.payment_info.legal_entity }
          }
        }
      }

      try {
        await this.inviteUser(payload)

        this.showNotification('success', 'Success', 'Invite has been successfully sent')
        this.$emit('add')
        this.closeModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  gap: 8px;
}

.ant-input-number {
  width: 100%;
}

.invite-user__layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  height: clamp(420px, calc(100vh - 220px), 720px);
  min-height: 0;
}

.invite-user__layout--fullscreen {
  height: 100%;
  max-height: none;
}

.invite-user__layout--without-gaps {
  grid-template-columns: minmax(0, 1fr);
}

.invite-user__form-column,
.invite-user__gaps-column {
  min-width: 0;
  min-height: 0;
  overflow: auto;
}

.invite-user__gaps-column {
  padding-left: 24px;
  border-left: 1px solid var(--border-color-base);
}

@media (max-width: 900px) {
  .invite-user__layout {
    display: flex;
    height: auto;
    max-height: calc(100vh - 220px);
    flex-direction: column;
    overflow: auto;
  }

  .invite-user__form-column,
  .invite-user__gaps-column {
    flex: 0 0 auto;
    overflow: visible;
  }

  .invite-user__gaps-column {
    padding-top: 24px;
    padding-left: 0;
    border-top: 1px solid var(--border-color-base);
    border-left: 0;
  }
}
</style>
